import apiClient from '../client';
import { APIResponse } from '@memriti/shared';

export interface CorporateVocabularyTerm {
  id: number;
  term: string;
  definition: string;
  context?: string;
  usage_examples?: string[];
  industry?: string;
  difficulty_level: string;
  synonyms?: string[];
  antonyms?: string[];
  company_tag?: {
    id: number;
    name: string;
    description?: string;
    color?: string;
  };
  color_code?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface VocabularyFilters {
  industry?: string;
  difficulty?: string;
  search?: string;
  company_tag_id?: string;
}

export const corporateVocabularyService = {
  // Get all vocabulary terms with optional filtering
  getAllTerms: async (filters?: VocabularyFilters): Promise<APIResponse<CorporateVocabularyTerm[]>> => {
    const params = new URLSearchParams();

    if (filters?.industry && filters.industry !== 'all') {
      params.append('industry', filters.industry);
    }
    if (filters?.difficulty && filters.difficulty !== 'all') {
      params.append('difficulty', filters.difficulty);
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }
    if (filters?.company_tag_id && filters.company_tag_id !== 'all') {
      params.append('company_tag_id', filters.company_tag_id);
    }

    const queryString = params.toString();
    const url = queryString ? `/api/v1/corporate-vocabulary?${queryString}` : '/api/v1/corporate-vocabulary';

    const response = await apiClient.get(url);
    return response.data;
  },

  // Get a specific vocabulary term
  getTerm: async (termId: number): Promise<APIResponse<CorporateVocabularyTerm>> => {
    const response = await apiClient.get(`/api/v1/corporate-vocabulary/${termId}`);
    return response.data;
  },

  // Get available industries for filtering
  getIndustries: async (): Promise<APIResponse<string[]>> => {
    const response = await apiClient.get('/api/v1/corporate-vocabulary/industries');
    return response.data;
  },

  // Get available difficulty levels for filtering
  getDifficultyLevels: async (): Promise<APIResponse<string[]>> => {
    const response = await apiClient.get('/api/v1/corporate-vocabulary/difficulty-levels');
    return response.data;
  },

  // Create a new vocabulary term (admin only)
  createTerm: async (termData: Partial<CorporateVocabularyTerm>): Promise<APIResponse<CorporateVocabularyTerm>> => {
    const response = await apiClient.post('/api/v1/corporate-vocabulary', termData);
    return response.data;
  },

  // Update a vocabulary term (admin only)
  updateTerm: async (termId: number, termData: Partial<CorporateVocabularyTerm>): Promise<APIResponse<CorporateVocabularyTerm>> => {
    const response = await apiClient.put(`/api/v1/corporate-vocabulary/${termId}`, termData);
    return response.data;
  },

  // Delete a vocabulary term (admin only)
  deleteTerm: async (termId: number): Promise<APIResponse<void>> => {
    const response = await apiClient.delete(`/api/v1/corporate-vocabulary/${termId}`);
    return response.data;
  },
};
